import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { ChevronDownIcon } from '../icons/TourIcons';
import type { PaymentsInvoice } from '../core/Types';
import styles from '../stylesheets/tenantInvoiceExpansion.module.scss';

interface TenantInvoiceExpansionProps {
  invoice: PaymentsInvoice;
  headerLeftMainText: string;
  defaultOpen?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);

const TenantInvoiceExpansion = ({
  invoice,
  headerLeftMainText,
  defaultOpen = true,
}: TenantInvoiceExpansionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const lineItemsTotal = useMemo(() => {
    return invoice.lineItems.reduce(
      (total, item) => total + item.amount * item.quantity,
      0
    );
  }, [invoice.lineItems]);
  const totalReceived = Math.max(invoice.total - invoice.balance, 0);

  return (
    <div
      className={classNames(styles.accordion, {
        [styles.open]: open,
      })}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <div className={styles.headerRow}>
          <span className={styles.headerTitle}>{headerLeftMainText}</span>
          <span className={styles.headerMeta}>{invoice.issuedDate}</span>
          <span></span>
          <span className={styles.headerAmount}>
            {formatCurrency(invoice.balance)}
          </span>
        </div>
        <span
          className={classNames(
            styles.chevron,
            open ? styles.chevronOpen : styles.chevronClosed
          )}
        >
          <ChevronDownIcon />
        </span>
      </button>

      {open && (
        <div className={styles.body}>
          <div className={styles.detailsCard}>
            <div className={styles.detailsRow}>
              <div>
                <span className={styles.detailsLabel}>Issued Date</span>
                <span className={styles.detailsValue}>
                  {invoice.issuedDate}
                </span>
              </div>
              <div className={styles.detailsRight}>
                <span className={styles.detailsLabel}>Due Date</span>
                <span className={styles.detailsValue}>{invoice.dueDate}</span>
              </div>
            </div>

            <div className={styles.dashedDivider} />

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className={styles.alignRight}>Qty</th>
                    <th className={styles.alignRight}>Amount</th>
                    <th className={styles.alignRight}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((item) => {
                    const total = item.amount * item.quantity;
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className={styles.itemName}>{item.itemType}</div>
                          {item.description && (
                            <div className={styles.itemMeta}>
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td className={styles.alignRight}>{item.quantity}</td>
                        <td className={styles.alignRight}>
                          {formatCurrency(item.amount)}
                        </td>
                        <td className={styles.alignRight}>
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.dashedDivider} />

            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total Amount</span>
              <span className={styles.totalValue}>
                {formatCurrency(lineItemsTotal)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantInvoiceExpansion;
